import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import BlockDisplayComponent from "../BlockDisplayComponent/BlockDisplayComponent";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";

const QrComponent = () => {
  const qrRef = useRef(null);
  const navigate = useNavigate();

  const outletContext = useOutletContext() || {};
  const { weddingData = {}, slug = "" } = outletContext;

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const weddingSlug = slug || weddingData?.slug || "";

  const isPremium = weddingData?.plan === "premium";
  const qrEnabled = weddingData?.addons?.qrCode === true;

  // Access rule:
  // premium users must have the QR add-on
  const hasAccess = isPremium && qrEnabled;

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!weddingSlug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", weddingSlug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading QR theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [weddingData, weddingSlug]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const qrValue = weddingSlug
    ? `https://www.planmyguest.com/sites/${weddingSlug}`
    : "https://www.planmyguest.com";

  const downloadQRCode = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;

    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");

    link.href = url;
    link.download = `${weddingSlug || "wedding"}-qr.png`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (themeLoading || !siteTheme) return null;

  if (!hasAccess) {
    return (
      <BlockDisplayComponent
        title="This feature is locked"
        description="Premium users can access it only when this add-on has been added to their package. Call 070 536 7980 to get this feature"
        primaryButtonText="Add this feature"
        secondaryButtonText="Go Back"
        onPrimaryClick={() => navigate("/premium")}
        onSecondaryClick={() => navigate(-1)}
      />
    );
  }

  return (
    <div
      style={{
        ...themeStyle,
        textAlign: "center",
        marginTop: "20px",
        padding: "10px",
      }}
    >
      <h3
        style={{
          marginBottom: "10px",
          color: "var(--theme-text)",
        }}
      >
        Scan to view wedding website
      </h3>

      <div
        ref={qrRef}
        style={{
          display: "inline-block",
          padding: "14px",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          border: "1px solid color-mix(in srgb, var(--theme-primary) 18%, white)",
        }}
      >
        <QRCodeCanvas
          value={qrValue}
          size={220}
          bgColor="#ffffff"
          fgColor="#000000"
          level="H"
          includeMargin={true}
        />
      </div>

      <p
        style={{
          marginTop: "12px",
          fontSize: "0.9rem",
          color: "var(--theme-text)",
          opacity: 0.7,
          wordBreak: "break-all",
        }}
      >
        {qrValue}
      </p>

      <div style={{ marginTop: "14px" }}>
        <ButtonComponent onClick={downloadQRCode} size="md">
          Download QR Code
        </ButtonComponent>
      </div>
    </div>
  );
};

export default QrComponent;